<app-modal
  [(open)]="open"
  [title]="title()"
  [subtitle]="subtitle()"
  size="xlarge"
  [minHeightPx]="680"
  [heightPx]="heightPx()"
  [flushBody]="true"
  [dismissable]="dismissable()"
  [interceptClose]="confirmClose()"
  (closeRequested)="onCloseRequested()"
  (closed)="closed.emit()"
>
  <ng-container *transloco="let t; read: 'common.wizard'">
    @if (saveExitLabel(); as sel) {
      <ng-template modalHeaderAction>
        <button
          type="button"
          (click)="saveExit.emit()"
          [disabled]="busy()"
          class="flex-none self-center text-[13px] font-semibold text-ink-2 hover:text-ink-1 disabled:opacity-40"
        >
          {{ sel }}
        </button>
      </ng-template>
    }

    <div class="flex min-h-0 flex-1 flex-col md:flex-row">
      <!-- MOBILE STEP STRIP — replaces the rail below md: the numbered circles
           in a horizontal row (same click/aria semantics), current label next
           to its circle, footer already shows "Step n of N". -->
      <nav
        class="flex flex-none items-center gap-2 overflow-x-auto border-b border-hairline px-5 py-3 md:hidden"
        [attr.aria-label]="t('steps')"
      >
        @for (v of stepViews(); track v.step.id()) {
          <button
            type="button"
            [disabled]="!v.clickable"
            (click)="goTo(v.step.id())"
            [attr.aria-current]="v.status === 'active' ? 'step' : null"
            [attr.aria-label]="v.step.label()"
            class="flex flex-none items-center gap-2"
            [class.cursor-default]="!v.clickable"
          >
            <span
              class="flex h-6 w-6 flex-none items-center justify-center rounded-pill text-[12px] font-bold"
              [class]="circleCls(v.status)"
            >
              @if (v.status === 'done') {
                <app-icon name="icon-checkmark" [size]="14" />
              } @else {
                {{ v.index + 1 }}
              }
            </span>
            @if (v.status === 'active') {
              <span
                class="whitespace-nowrap text-[12px] font-bold uppercase leading-4 tracking-[0.03em] text-ink-1"
              >
                {{ v.step.label() }}
              </span>
            }
          </button>
        }
      </nav>

      <!-- RAIL — DS wizard: white rail, uppercase step labels, filled action
           dot on the active step, outlined circles while upcoming. -->
      <nav
        class="hidden w-[264px] flex-none overflow-y-auto px-3 py-5 md:block"
        [attr.aria-label]="t('steps')"
      >
        <ol class="flex list-none flex-col gap-1.5">
          @for (v of stepViews(); track v.step.id()) {
            <li>
              <button
                type="button"
                [disabled]="!v.clickable"
                (click)="goTo(v.step.id())"
                [attr.aria-current]="v.status === 'active' ? 'step' : null"
                class="flex w-full items-start gap-3 rounded-control px-3 py-2 text-left"
                [class.hover:bg-sunken]="v.clickable"
                [class.cursor-default]="!v.clickable"
              >
                <span
                  class="flex h-6 w-6 flex-none items-center justify-center rounded-pill text-[12px] font-bold"
                  [class]="circleCls(v.status)"
                >
                  @if (v.status === 'done') {
                    <app-icon name="icon-checkmark" [size]="14" />
                  } @else {
                    {{ v.index + 1 }}
                  }
                </span>
                <span class="min-w-0 pt-0.5">
                  <span
                    class="block text-[12px] font-bold uppercase leading-4 tracking-[0.03em]"
                    [class.text-ink-1]="v.status === 'active' || v.status === 'done'"
                    [class.text-ink-2]="v.status === 'upcoming'"
                    [class.text-danger]="v.status === 'error'"
                  >
                    {{ v.step.label()
                    }}@if (v.step.optional()) {
                      <span class="font-normal normal-case text-ink-3"> · {{ t('optional') }}</span>
                    }
                  </span>
                  @if (v.step.sublabel()) {
                    <span class="block text-[11px] leading-4 text-ink-3">{{ v.step.sublabel() }}</span>
                  }
                </span>
              </button>
            </li>
          }
        </ol>
      </nav>

      <!-- ACTIVE STEP — strong vertical divider against the rail per the
           design (paints with the action fill so it flips with the theme). -->
      <div
        class="min-w-0 flex-1 overflow-y-auto px-5 py-5 md:border-l-2 md:px-9 md:py-6"
        style="border-color: var(--action-fill)"
      >
        @if (activeStep(); as s) {
          <div class="max-w-[560px]">
            <h2 class="ds-h2 mb-1.5 text-ink-1">{{ s.heading() || s.label() }}</h2>
            @if (s.description()) {
              <p class="mb-7 text-[14px] leading-[22px] text-ink-1">{{ s.description() }}</p>
            }
            @if (s.error(); as err) {
              <p class="mb-4 rounded-control bg-danger-surface px-4 py-3 text-[13px] font-semibold text-danger">
                {{ err }}
              </p>
            }
            @if (s.content(); as tpl) {
              <ng-container [ngTemplateOutlet]="tpl" />
            }
          </div>
        }
      </div>
    </div>

    <!-- FOOTER — matches the DS ModalFooter button treatment in app-modal.
         TODO(ds): adopt app-button for the footer buttons once it lands. -->
    <ng-template modalFooter>
      <div class="flex flex-1 items-center justify-between gap-4">
        <span class="hidden text-[12px] text-ink-3 sm:inline">
          @if (stepList().length > 0) {
            {{ t('stepOf', { n: activeIndex() + 1, total: stepList().length }) }}
          }
        </span>
        <div class="flex items-center gap-4">
          @if (activeIndex() > 0) {
            <button
              type="button"
              (click)="back()"
              [disabled]="busy()"
              class="flex h-12 items-center rounded-control bg-card px-4 text-button font-semibold text-action shadow-raised hover:bg-sunken disabled:opacity-40"
            >
              {{ t('back') }}
            </button>
          } @else if (dismissable()) {
            <!-- First step: Cancel takes the Back slot (same dirty-check close
                 flow as the header X / Escape). -->
            <button
              type="button"
              (click)="onCloseRequested()"
              [disabled]="busy()"
              class="flex h-12 items-center gap-1.5 rounded-control bg-card px-4 text-button font-semibold text-action shadow-raised hover:bg-sunken disabled:opacity-40"
            >
              <app-icon name="icon-x" [size]="16" />
              {{ t('cancel') }}
            </button>
          }
          <button
            type="button"
            (click)="next()"
            [disabled]="busy()"
            class="flex h-12 items-center gap-1.5 rounded-control bg-action px-4 text-button font-semibold text-action-text shadow-raised hover:brightness-90 disabled:opacity-40"
          >
            @if (activeStep()?.nextLabel(); as nl) {
              {{ nl }}
            } @else if (isLast()) {
              {{ finishLabel() ?? t('finish') }}
            } @else {
              {{ t('next') }}
            }
            <app-icon name="icon-arrow-right" [size]="16" />
          </button>
        </div>
      </div>
    </ng-template>
  </ng-container>
</app-modal>
<!-- Steps render nothing; keep them projected for hygiene. -->
<div hidden><ng-content /></div>
